import React, { useState } from 'react';
import {
	Box,
	Center,
	Grid,
	GridItem,
	Tabs,
	TabList,
	Tab,
	TabPanels,
	TabPanel,
	VStack,
	Icon,
	Text,
	Button,
} from '@chakra-ui/react';
import {
	FaChartLine,
	FaUsers,
	FaBullhorn,
	FaAddressBook,
} from 'react-icons/fa';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import { useDashboardData } from '../../../hooks/useDashboard';
import {
	DashboardHeader,
	OnboardingChecklist,
	KeyMetrics,
	CampaignPerformanceOverview,
	DashboardCharts,
	OverviewTab,
	TeamTab,
	CampaignsTab,
	ContactsTab,
	QuickActions,
	DashboardSkeleton,
} from './index';
import CampaignEmailSentLimitBanner from '../../layout/CampaignEmailSentLimitBanner';

const CompanyAdminAndEmployeeDashboard = () => {
	const { companyId } = useAuthenticatedUserInfo();
	const [activeTab, setActiveTab] = useState(0);

	const {
		data: dashboardData,
		isLoading,
		error,
		refetch,
	} = useDashboardData(companyId);

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	if (error) {
		return (
			<Center h='100vh' px={6}>
				<VStack spacing={4} maxW='md' textAlign='center'>
					<Text color='red.500' fontWeight='semibold'>
						Error loading dashboard data
					</Text>
					<Text fontSize='sm' color='gray.600'>
						{error.message}
					</Text>
					<Button colorScheme='teal' onClick={() => refetch()}>
						Try again
					</Button>
				</VStack>
			</Center>
		);
	}

	if (!dashboardData) {
		return (
			<Center h='100vh'>
				<Text color='gray.600'>No dashboard data available</Text>
			</Center>
		);
	}

	const {
		companyStats,
		campaignSummaries,
		recentCampaigns,
		recentProjects,
		recentLeads,
		teamMembers,
		user,
		company,
		lists,
		analytics,
		campaigns,
	} = dashboardData;

	return (
		<Box w='100%'>
			<VStack spacing={6} align='stretch'>
				<DashboardHeader company={company} user={user} />
				
					<CampaignEmailSentLimitBanner />

				<OnboardingChecklist />

				<KeyMetrics stats={companyStats} />

				<Grid templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }} gap={6}>
					<GridItem>
						<CampaignPerformanceOverview stats={companyStats} />
					</GridItem>
					<GridItem>
						<QuickActions />
					</GridItem>
				</Grid>

				<DashboardCharts analytics={analytics} campaigns={campaigns} />

				<Tabs
					index={activeTab}
					onChange={setActiveTab}
					colorScheme='teal'
					variant='enclosed'>
					<Box bg='white' borderRadius='2xl' border='1px solid' borderColor='gray.200' p={1}>
						<TabList border='none' flexWrap='wrap' gap={1}>
							<Tab borderRadius='xl' _selected={{ bg: 'teal.50', color: 'teal.700' }}>
								<Icon as={FaChartLine} mr={2} />
								Overview
							</Tab>
							<Tab borderRadius='xl' _selected={{ bg: 'teal.50', color: 'teal.700' }}>
								<Icon as={FaUsers} mr={2} />
								Team
							</Tab>
							<Tab borderRadius='xl' _selected={{ bg: 'teal.50', color: 'teal.700' }}>
								<Icon as={FaBullhorn} mr={2} />
								Campaigns
							</Tab>
							<Tab borderRadius='xl' _selected={{ bg: 'teal.50', color: 'teal.700' }}>
								<Icon as={FaAddressBook} mr={2} />
								Contacts
							</Tab>
						</TabList>
					</Box>

					<TabPanels mt={4} px={0}>
						<TabPanel px={0}>
							<OverviewTab
								recentCampaigns={recentCampaigns}
								recentProjects={recentProjects}
								recentLeads={recentLeads}
								teamMembers={teamMembers}
							/>
						</TabPanel>

						<TabPanel px={0}>
							<TeamTab teamMembers={teamMembers} />
						</TabPanel>

						<TabPanel px={0}>
							<CampaignsTab campaigns={campaignSummaries} />
						</TabPanel>

						<TabPanel px={0}>
							<ContactsTab lists={lists} companyId={companyId} />
						</TabPanel>
					</TabPanels>
				</Tabs>
			</VStack>
		</Box>
	);
};

export default CompanyAdminAndEmployeeDashboard;
